import { PresentationBundleKind } from '@/types/schema';

import { BundleSaveDescriptor } from '../presentationSave.types';
import { assetDistributionBundleDescriptor } from './assetDistributionBundle';
import { balanceSheetBundleDescriptor } from './balanceSheetBundle';
import { beneficiaryReportBundleDescriptor } from './beneficiaryReportBundle';
import { businessOverviewBundleDescriptor } from './businessOverviewBundle';
import { coverSlideBundleDescriptor } from './coverSlideBundle';
import { entitySummaryBundleDescriptor } from './entitySummaryBundle';
import { estateInsightsBundleDescriptor } from './estateInsightsBundle';
import {
  customPageBundleDescriptor,
  dividerBundleDescriptor,
  entityGraphViewBundleDescriptor,
} from './flatConfigBundles';
import { healthcareDirectiveBundleDescriptor } from './healthcareDirectiveBundle';
import {
  documentsReviewedBundleDescriptor,
  entitiesOverviewBundleDescriptor,
  familyOverviewBundleDescriptor,
  planningOpportunitiesBundleDescriptor,
} from './listBundleDescriptor';
import { noConfigBundleDescriptor } from './noConfigBundles';
import { powerOfAttorneyBundleDescriptor } from './powerOfAttorneyBundle';
import { trustOverviewBundleDescriptor } from './trustOverviewBundle';
import { waterfallComparisonBundleDescriptor } from './waterfallComparisonBundle';
import { waterfallOverviewBundleDescriptor } from './waterfallOverviewBundle';
import { willBundleDescriptor } from './willBundle';

/**
 * Every bundle kind must define how incremental save handles its configuration.
 */
export const BUNDLE_SAVE_DESCRIPTORS: Record<
  PresentationBundleKind,
  BundleSaveDescriptor
> = {
  [PresentationBundleKind.DiagramBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.DocumentSummaryBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.EducationPageBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.FamilyTreeBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.LiabilitiesBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.LifeInsuranceBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.LuminaryDisclaimerBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.ProfessionalTeamBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.StandaloneDisclaimerBundle]: noConfigBundleDescriptor,
  [PresentationBundleKind.TableOfContentsBundle]: noConfigBundleDescriptor,

  // Kinds whose configuration is flat scalars, with no child rows.
  [PresentationBundleKind.CoverSlideBundle]: coverSlideBundleDescriptor,
  [PresentationBundleKind.CustomPageBundle]: customPageBundleDescriptor,
  [PresentationBundleKind.DividerBundle]: dividerBundleDescriptor,
  [PresentationBundleKind.EntityGraphViewBundle]:
    entityGraphViewBundleDescriptor,

  // Kinds whose configuration is a title plus one list of keyed rows.
  [PresentationBundleKind.DocumentsReviewedBundle]:
    documentsReviewedBundleDescriptor,
  [PresentationBundleKind.FamilyOverviewBundle]: familyOverviewBundleDescriptor,
  [PresentationBundleKind.PlanningOpportunitiesBundle]:
    planningOpportunitiesBundleDescriptor,
  [PresentationBundleKind.EntitiesOverviewBundle]:
    entitiesOverviewBundleDescriptor,

  // Kinds shaped as a title plus cards of label/body items.
  [PresentationBundleKind.TrustOverviewBundle]: trustOverviewBundleDescriptor,
  [PresentationBundleKind.BusinessOverviewBundle]:
    businessOverviewBundleDescriptor,
  [PresentationBundleKind.WillBundle]: willBundleDescriptor,
  [PresentationBundleKind.PowerOfAttorneyBundle]:
    powerOfAttorneyBundleDescriptor,
  [PresentationBundleKind.HealthcareDirectiveBundle]:
    healthcareDirectiveBundleDescriptor,

  [PresentationBundleKind.WaterfallOverviewBundle]:
    waterfallOverviewBundleDescriptor,
  [PresentationBundleKind.EntitySummaryBundle]: entitySummaryBundleDescriptor,

  // The fifth card-and-item kind, with two extra configuration scalars.
  [PresentationBundleKind.AssetDistributionBundle]:
    assetDistributionBundleDescriptor,

  // Scalars plus a set of foreign keys, which `diffUnordered` writes as adds and
  // removes rather than as an ordered list.
  [PresentationBundleKind.EstateInsightsBundle]: estateInsightsBundleDescriptor,

  // Scalars plus a set of column ids, replaced wholesale.
  [PresentationBundleKind.BalanceSheetBundle]: balanceSheetBundleDescriptor,

  // Two sets of beneficiary ids, each splitting across a profile edge and an
  // organization edge.
  [PresentationBundleKind.BeneficiaryReportBundle]:
    beneficiaryReportBundleDescriptor,

  // Child rows with no `sortOrder` column, so any change to the list's shape
  // rewrites it.
  [PresentationBundleKind.WaterfallComparisonBundle]:
    waterfallComparisonBundleDescriptor,
};
